// Auto-generated lightweight class for Group
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { GroupReadAPI, GroupWriteAPI, GroupReadAPISchema, GroupWriteAPISchema } from '../types/group';
import { ModelRegistry } from '../../registries/model-registry';
import { GroupAt, MemberOf, MemberOfClosure } from '../../relationships/relationship';

import { User } from './user';
import { SecurityScope } from '../../shared/api/securityscope';



export interface Group extends Partial<GroupReadAPI> {}
export class Group extends NeoNodeBase<GroupReadAPI, GroupWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "Group";
  static readonly readSchema = GroupReadAPISchema;
  static readonly writeSchema = GroupWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    closure_holders: { target: "User", cardinality: "many", isParent: false, relClass: "MemberOfClosure", direction: "in" },
    scope: { target: "SecurityScope", cardinality: "one", isParent: true, relClass: "GroupAt", direction: "out" },
    holders: { target: "User", cardinality: "many", isParent: false, relClass: "MemberOf", direction: "in" },
  };

  protected get modelName(): string {
    return 'Group';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return Group.label;
  }


  constructor(data: Partial<GroupWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: GroupReadAPI, loadContext?: Record<string, any>): Group {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { holders, closure_holders, scope, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = GroupReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, holders: holders, closure_holders: closure_holders, scope: scope };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return Group.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadManyRelationship(processed, 'holders', 'User', ModelRegistry);
    loadManyRelationship(processed, 'closure_holders', 'User', ModelRegistry);
    loadOneRelationship(processed, 'scope', 'SecurityScope', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new Group(processed as any);

    return instance;
  }


  /**
   * Delete this instance from the server.
   *
   * @param options - Delete options
   * @param options.cascade - Whether to cascade delete related nodes (default: true for most models)
   * @param options.dryRun - If true, return what would be deleted without actually deleting
   * @returns DeleteResult with deleted IDs and count
   *
   * @example
   * ```typescript
   * const group = await Group.get(id).first();
   * await group.delete();  // Delete with default cascade
   *
   * // Or with options
   * const result = await group.delete({ cascade: false, dryRun: true });
   * console.log('Would delete:', result.deletedIds);
   * ```
   */
  async delete(options: {
    cascade?: boolean;
    dryRun?: boolean;
  } = {}): Promise<{ deletedIds: string[]; count: number }> {
    return this._deleteInstance(options);
  }

  // Relationship manager for 'holders' - Python ORM style (cached)
  get holders(): RelationshipManager<this, User> {
    return this.getOrCreateRelationshipManager('holders', () => {
      return new RelationshipManager(this, 'holders', 'many', MemberOf as typeof Relationship);
    });
  }

  // Relationship manager for 'scope' - Python ORM style (cached)
  get scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('scope', () => {
      return new RelationshipManager(this, 'scope', 'one', GroupAt as typeof Relationship);
    });
  }

  // Relationship manager for 'closure_holders' - Python ORM style (cached)
  get closure_holders(): RelationshipManager<this, User> {
    return this.getOrCreateRelationshipManager('closure_holders', () => {
      return new RelationshipManager(this, 'closure_holders', 'many', MemberOfClosure as typeof Relationship);
    });
  }

}
